import React from 'react';
import Select from 'react-select';
import styled from 'styled-components';
import { RefreshCw, Database, AlertCircle, CheckCircle } from 'lucide-react';
import { Filial, FilialSelectorProps } from '@/types';

const Container = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px;
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  margin-bottom: 24px;
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
    gap: 10px;
    padding: 12px;
  }
`;

const Label = styled.label`
  font-weight: 600;
  color: #374151;
  font-size: 14px;
  min-width: 120px;
  @media (max-width: 768px) {
    min-width: 0;
  }
`;

const SelectContainer = styled.div`
  flex: 1;
  min-width: 250px;
  @media (max-width: 768px) {
    min-width: 0;
    width: 100%;
  }
`;

const StatusIndicator = styled.div<{ status: Filial['status'] }>`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  padding: 4px 8px;
  border-radius: 4px;
  font-weight: 500;
  
  ${({ status }) => {
    switch (status) {
      case 'connected':
        return `
          color: #059669;
          background-color: #d1fae5;
        `;
      case 'disconnected':
        return `
          color: #dc2626;
          background-color: #fee2e2;
        `;
      case 'connecting':
        return `
          color: #d97706;
          background-color: #fef3c7;
        `;
      default:
        return `
          color: #6b7280;
          background-color: #f3f4f6;
        `;
    }
  }}
`;

// Badge lateral que será ocultada no mobile para economizar espaço
const StatusAside = styled(StatusIndicator)`
  @media (max-width: 768px) {
    display: none;
  }
`;

const RefreshButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border: none;
  border-radius: 6px;
  background-color: #f3f4f6;
  color: #6b7280;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background-color: #e5e7eb;
    color: #374151;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
  @media (max-width: 768px) {
    width: 36px;
    height: 36px;
  }
`;

const customSelectStyles = {
  control: (provided: any, state: any) => ({
    ...provided,
    border: '1px solid #d1d5db',
    borderRadius: '6px',
    padding: '2px',
    boxShadow: state.isFocused ? '0 0 0 3px rgba(59, 130, 246, 0.1)' : 'none',
    borderColor: state.isFocused ? '#3b82f6' : '#d1d5db',
    '&:hover': {
      borderColor: '#9ca3af'
    }
  }),
  option: (provided: any, state: any) => ({
    ...provided,
    display: 'flex',
    alignItems: 'center',
    padding: '12px',
    backgroundColor: state.isSelected 
      ? '#3b82f6' 
      : state.isFocused 
        ? '#f3f4f6' 
        : 'white',
    color: state.isSelected ? 'white' : '#374151',
    cursor: 'pointer'
  }),
  singleValue: (provided: any) => ({
    ...provided,
    display: 'flex',
    alignItems: 'center'
  })
};

const getStatusIcon = (status: Filial['status']) => {
  switch (status) {
    case 'connected':
      return <CheckCircle size={14} />;
    case 'disconnected':
      return <AlertCircle size={14} />;
    case 'connecting':
      return <RefreshCw size={14} className="animate-spin" />;
    default:
      return <Database size={14} />;
  }
};

const getStatusText = (status: Filial['status']) => {
  switch (status) {
    case 'connected':
      return 'Conectado';
    case 'disconnected':
      return 'Desconectado';
    case 'connecting':
      return 'Conectando...';
    default:
      return 'Desconhecido';
  }
};

const FilialSelector: React.FC<FilialSelectorProps> = ({
  filialSelecionada,
  filiais,
  onFilialChange,
  isLoading = false
}) => {
  const formatOptionLabel = (filial: Filial) => (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        <Database size={16} />
        <span>{filial.nome}</span>
      </div>
      <StatusIndicator status={filial.status}>
        {getStatusIcon(filial.status)}
        {getStatusText(filial.status)}
      </StatusIndicator>
    </div>
  );

  const handleChange = (selectedOption: any) => {
    if (selectedOption) {
      const filial = filiais.find(f => f.id === selectedOption.value);
      if (filial) {
        onFilialChange(filial);
      }
    }
  };

  const handleRefresh = () => {
    // Esta função será implementada no componente pai
    window.location.reload();
  };

  const options = filiais.map(filial => ({
    value: filial.id,
    label: filial.nome,
    filial: filial
  }));

  const selectedValue = filialSelecionada ? {
    value: filialSelecionada.id,
    label: filialSelecionada.nome,
    filial: filialSelecionada
  } : null;

  return (
    <Container>
      <Label htmlFor="filial-selector">
        Filial Ativa:
      </Label>
      
      <SelectContainer>
        <Select
          id="filial-selector"
          value={selectedValue}
          onChange={handleChange}
          options={options}
          formatOptionLabel={(option: any) => formatOptionLabel(option.filial)}
          styles={customSelectStyles}
          isLoading={isLoading}
          isDisabled={isLoading}
          placeholder="Selecione uma filial..."
          noOptionsMessage={() => "Nenhuma filial encontrada"}
          loadingMessage={() => "Carregando filiais..."}
        />
      </SelectContainer>

      {filialSelecionada && (
        <StatusAside status={filialSelecionada.status}>
          {getStatusIcon(filialSelecionada.status)}
          {getStatusText(filialSelecionada.status)}
        </StatusAside>
      )}

      <RefreshButton
        onClick={handleRefresh}
        disabled={isLoading}
        title="Atualizar lista de filiais"
      >
        <RefreshCw size={18} className={isLoading ? 'animate-spin' : ''} />
      </RefreshButton>
    </Container>
  );
};

export default FilialSelector;